import React, { useEffect, useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { messengerApi } from '../lib/messengerApi';
import type { LinkPreviewCard as LinkPreview } from '../types';
import { cn } from '@/lib/utils';

interface Props {
  url: string;
  isMine: boolean;
}

// Task #1660 — Open Graph preview con cache server-side (tenant-scoped).
// La card si renderizza sotto il messaggio (link rilevato dal Composer).
// Lazy fetch al mount: il payload è già cached dopo il primo render della
// stessa URL nella conversazione (TTL 7gg).

export function LinkPreviewCard({ url, isMine }: Props) {
  const [data, setData] = useState<LinkPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    messengerApi
      .linkPreview(url)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setData(res.data);
          // Card vuota = nessun OG meta utile → non mostrare nulla.
          if (!res.data.og_title && !res.data.og_image && !res.data.og_description) {
            setFailed(true);
          }
        } else {
          setFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [url]);

  if (loading) {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Anteprima link…
      </div>
    );
  }
  if (failed || !data) return null;

  const host = (() => {
    try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
  })();

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      className={cn(
        'mt-2 block max-w-md rounded-xl overflow-hidden border transition-all hover:shadow-md',
        isMine
          ? 'bg-blue-500/10 border-blue-300/40 text-blue-50 hover:bg-blue-500/15'
          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
      )}
    >
      {data.og_image && (
        <div className="aspect-[1.91/1] w-full overflow-hidden bg-gray-100 dark:bg-gray-900">
          <img
            src={data.og_image}
            alt={data.og_title || ''}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        </div>
      )}
      <div className="p-3 space-y-1">
        <div className={cn('text-[10px] uppercase tracking-wide flex items-center gap-1', isMine ? 'text-blue-100/80' : 'text-muted-foreground')}>
          <ExternalLink className="h-3 w-3" />
          {data.og_site_name || host}
        </div>
        {data.og_title && (
          <div className={cn('text-sm font-semibold line-clamp-2', isMine ? 'text-white' : 'text-gray-900 dark:text-gray-100')}>
            {data.og_title}
          </div>
        )}
        {data.og_description && (
          <div className={cn('text-xs line-clamp-2', isMine ? 'text-blue-100/90' : 'text-muted-foreground')}>
            {data.og_description}
          </div>
        )}
      </div>
    </a>
  );
}
